/**
 * LightingPanel — Dev tool for adjusting interior light parameters in real-time.
 *
 * Connects to the first active Room3DViewer's interior PointLight via the
 * global lighting registry. Changes are applied immediately and re-rendered.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as THREE from 'three';
import { getRegisteredLight, onLightChange, LightingRegistryEntry, getWallMaterials } from './lightingRegistry';
import { ENV_IMAGES, loadEnvImage } from '../ui/room3d/sceneBuilder';
import { FloorPanel } from './FloorPanel';

interface SliderDef {
  label: string;
  key: string;
  min: number;
  max: number;
  step: number;
  get: (e: LightingRegistryEntry) => number;
  set: (e: LightingRegistryEntry, v: number) => void;
}

/** Helper: dispose and null out the shadow map to force Three.js to rebuild it.
 *  Must render twice: first pass rebuilds the shadow map, second uses it. */
function invalidateShadowMap(e: LightingRegistryEntry) {
  if (e.light.shadow.map) {
    e.light.shadow.map.dispose();
    (e.light.shadow as any).map = null;
  }
  e.renderer.shadowMap.needsUpdate = true;
  // Double-render: first rebuilds shadow map, second renders with new shadows
  e.renderScene();
  e.renderScene();
}

const SLIDERS: SliderDef[] = [
  {
    label: 'Intensity',
    key: 'intensity',
    min: 0, max: 10, step: 0.05,
    get: e => e.light.intensity,
    set: (e, v) => { e.light.intensity = v; },
  },
  {
    label: 'Decay',
    key: 'decay',
    min: 0, max: 4, step: 0.05,
    get: e => e.light.decay,
    set: (e, v) => { e.light.decay = v; },
  },
  {
    label: 'Distance (0=inf)',
    key: 'distance',
    min: 0, max: 100, step: 0.5,
    get: e => e.light.distance,
    set: (e, v) => { e.light.distance = v; },
  },
  {
    label: 'Shadow Radius',
    key: 'shadowRadius',
    min: 0, max: 30, step: 0.5,
    get: e => e.light.shadow.radius,
    set: (e, v) => { e.light.shadow.radius = v; invalidateShadowMap(e); },
  },
  {
    label: 'Shadow Bias (×10000)',
    key: 'shadowBias',
    min: -50, max: 50, step: 0.1,
    get: e => e.light.shadow.bias * 10000,
    set: (e, v) => { e.light.shadow.bias = v / 10000; invalidateShadowMap(e); },
  },
  {
    label: 'Shadow Normal Bias',
    key: 'shadowNormalBias',
    min: 0, max: 0.5, step: 0.005,
    get: e => e.light.shadow.normalBias,
    set: (e, v) => { e.light.shadow.normalBias = v; invalidateShadowMap(e); },
  },
  {
    label: 'Position Y (%H)',
    key: 'posY',
    min: 0, max: 100, step: 1,
    get: e => {
      if (e.viewerType === 'ceiling') {
        // Ceiling light is at negative Y; show as positive percentage of distance below surface
        return Math.abs(e.light.position.y / e.roomDimensions.height) * 100;
      }
      return (e.light.position.y / e.roomDimensions.height) * 100;
    },
    set: (e, v) => {
      if (e.viewerType === 'ceiling') {
        e.light.position.y = -(v / 100) * e.roomDimensions.height;
      } else {
        e.light.position.y = (v / 100) * e.roomDimensions.height;
      }
    },
  },
  {
    label: 'Hemi Intensity',
    key: 'hemiIntensity',
    min: 0, max: 3, step: 0.05,
    get: e => e.hemiLight?.intensity ?? 0,
    set: (e, v) => { if (e.hemiLight) e.hemiLight.intensity = v; },
  },
  {
    label: 'Shadow Map (px)',
    key: 'shadowMapSize',
    min: 256, max: 4096, step: 256,
    get: e => e.light.shadow.mapSize.width,
    set: (e, v) => {
      e.light.shadow.mapSize.width = v;
      e.light.shadow.mapSize.height = v;
      invalidateShadowMap(e);
    },
  },
];

const COLOR_PRESETS: { label: string; hex: string }[] = [
  { label: 'Warm', hex: '#fff5e6' },
  { label: 'Neutral', hex: '#ffffff' },
  { label: 'Cool', hex: '#e6f0ff' },
  { label: 'Daylight', hex: '#fffbe6' },
  { label: 'Tungsten', hex: '#ffd6aa' },
];

export const LightingPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [entry, setEntry] = useState<LightingRegistryEntry | null>(getRegisteredLight());
  const [, forceUpdate] = useState(0);
  const rafRef = useRef<number | null>(null);

  const [rotation, setRotation] = useState({ x: 0, y: 0 });

  // Subscribe to light registry changes
  useEffect(() => {
    const unsub = onLightChange(() => setEntry(getRegisteredLight()));
    return unsub;
  }, []);

  // Poll rotation from the active viewer
  useEffect(() => {
    if (!entry) return;
    const interval = setInterval(() => {
      const r = entry.getRotation();
      setRotation(r);
    }, 200);
    return () => clearInterval(interval);
  }, [entry]);

  // Continuously re-render the 3D scene when panel is open (for smooth slider dragging)
  const scheduleRender = useCallback(() => {
    if (entry) entry.renderScene();
  }, [entry]);

  const handleSliderChange = useCallback((slider: SliderDef, value: number) => {
    if (!entry) return;
    slider.set(entry, value);
    scheduleRender();
    forceUpdate(n => n + 1);
  }, [entry, scheduleRender]);

  const handleColorChange = useCallback((hex: string) => {
    if (!entry) return;
    entry.light.color.set(hex);
    scheduleRender();
    forceUpdate(n => n + 1);
  }, [entry, scheduleRender]);

  const handleToggleShadows = useCallback(() => {
    if (!entry) return;
    entry.light.castShadow = !entry.light.castShadow;
    // Force shadow map rebuild
    if (entry.light.shadow.map) {
      entry.light.shadow.map.dispose();
      (entry.light.shadow as any).map = null;
    }
    entry.renderer.shadowMap.needsUpdate = true;
    // Double-render for shadow map rebuild
    entry.renderScene();
    entry.renderScene();
    forceUpdate(n => n + 1);
  }, [entry]);

  // --- Light Probe Controls ---
  const [probeIntensity, setProbeIntensity] = useState(0.4);
  const [envMapIntensity, setEnvMapIntensity] = useState(0.5);
  const [envMapEnabled, setEnvMapEnabled] = useState(true);
  const [sceneBackground, setSceneBackground] = useState(false);

  const [selectedEnvIdx, setSelectedEnvIdx] = useState(2);
  const [floorPanelOpen, setFloorPanelOpen] = useState(false);
  const [matRoughness, setMatRoughness] = useState(0.3);
  const [matMetalness, setMatMetalness] = useState(0.1);
  const [matEmissive, setMatEmissive] = useState(0.4);

  // Sync state from live scene when entry changes
  useEffect(() => {
    if (!entry) return;
    if (entry.probe) setProbeIntensity(entry.probe.intensity);
    const mats = getWallMaterials(entry);
    if (mats.length > 0) {
      setMatRoughness(mats[0].roughness);
      setMatMetalness(mats[0].metalness);
      setMatEmissive(mats[0].emissiveIntensity);
    }
  }, [entry]);

  const handleEnvImageChange = useCallback((idx: number) => {
    if (!entry) return;
    setSelectedEnvIdx(idx);
    loadEnvImage(entry.renderer, ENV_IMAGES[idx].path, (envMap, newProbe) => {
      if (entry.probe) {
        const savedIntensity = entry.probe.intensity;
        entry.probe.copy(newProbe);
        entry.probe.intensity = savedIntensity;
      }
      entry.envMap = envMap;
      entry.scene.environment = envMapEnabled ? envMap : null;
      if (sceneBackground) entry.scene.background = envMap;
      scheduleRender();
      forceUpdate(n => n + 1);
    });
  }, [entry, envMapEnabled, sceneBackground, scheduleRender]);

  const handleProbeIntensity = useCallback((v: number) => {
    if (!entry?.probe) return;
    entry.probe.intensity = v;
    setProbeIntensity(v);
    scheduleRender();
  }, [entry, scheduleRender]);

  const handleEnvMapIntensity = useCallback((v: number) => {
    if (!entry) return;
    setEnvMapIntensity(v);
    for (const mat of getWallMaterials(entry)) {
      mat.envMapIntensity = v;
    }
    scheduleRender();
  }, [entry, scheduleRender]);

  const handleToggleEnvMap = useCallback(() => {
    if (!entry?.envMap) return;
    const next = !envMapEnabled;
    setEnvMapEnabled(next);
    // scene.environment is the single source of truth
    entry.scene.environment = next ? entry.envMap : null;
    scheduleRender();
    forceUpdate(n => n + 1);
  }, [entry, envMapEnabled, scheduleRender]);

  const handleToggleBackground = useCallback(() => {
    if (!entry?.envMap) return;
    const next = !sceneBackground;
    setSceneBackground(next);
    // Only toggle the visible background image — never touch scene.environment
    entry.scene.background = next ? entry.envMap : new THREE.Color(0xf9fafb);
    scheduleRender();
    forceUpdate(n => n + 1);
  }, [entry, sceneBackground, scheduleRender]);

  const handleMaterialRoughness = useCallback((v: number) => {
    if (!entry) return;
    setMatRoughness(v);
    for (const mat of getWallMaterials(entry)) { mat.roughness = v; }
    scheduleRender();
  }, [entry, scheduleRender]);

  const handleMaterialMetalness = useCallback((v: number) => {
    if (!entry) return;
    setMatMetalness(v);
    for (const mat of getWallMaterials(entry)) { mat.metalness = v; }
    scheduleRender();
  }, [entry, scheduleRender]);

  const handleEmissiveIntensity = useCallback((v: number) => {
    if (!entry) return;
    setMatEmissive(v);
    for (const mat of getWallMaterials(entry)) { mat.emissiveIntensity = v; }
    scheduleRender();
  }, [entry, scheduleRender]);

  const copySettings = useCallback(() => {
    if (!entry) return;
    const wallMats = getWallMaterials(entry);
    const firstMat = wallMats[0];
    const floorMat = entry.floorMesh?.material as THREE.MeshStandardMaterial | undefined;
    const floorTex = floorMat?.map;

    // Find which directional lights exist
    const dirLights: { intensity: number; position: { x: number; y: number; z: number } }[] = [];
    entry.scene.traverse(child => {
      if (child instanceof THREE.DirectionalLight) {
        dirLights.push({
          intensity: +child.intensity.toFixed(2),
          position: { x: +child.position.x.toFixed(1), y: +child.position.y.toFixed(1), z: +child.position.z.toFixed(1) },
        });
      }
    });

    const settings = {
      pointLight: {
        intensity: +entry.light.intensity.toFixed(2),
        decay: +entry.light.decay.toFixed(2),
        distance: +entry.light.distance.toFixed(1),
        color: '#' + entry.light.color.getHexString(),
        castShadow: entry.light.castShadow,
        shadowRadius: +entry.light.shadow.radius.toFixed(1),
        shadowBias: +entry.light.shadow.bias.toFixed(5),
        shadowNormalBias: +entry.light.shadow.normalBias.toFixed(4),
        shadowMapSize: entry.light.shadow.mapSize.width,
        positionYPercent: +(Math.abs(entry.light.position.y / entry.roomDimensions.height) * 100).toFixed(0),
      },
      hemiIntensity: +(entry.hemiLight?.intensity ?? 0).toFixed(3),
      directionalLights: dirLights,
      probe: {
        intensity: +(entry.probe?.intensity ?? 0).toFixed(2),
        available: !!entry.probe,
      },
      envMap: {
        enabled: envMapEnabled,
        intensity: +envMapIntensity.toFixed(2),
        asBackground: sceneBackground,
        selectedImage: ENV_IMAGES[selectedEnvIdx]?.label ?? null,
        selectedPath: ENV_IMAGES[selectedEnvIdx]?.path ?? null,
      },
      material: {
        roughness: +(firstMat?.roughness ?? 0).toFixed(2),
        metalness: +(firstMat?.metalness ?? 0).toFixed(2),
        emissiveIntensity: +(firstMat?.emissiveIntensity ?? 0).toFixed(2),
        emissiveColor: firstMat ? '#' + firstMat.emissive.getHexString() : null,
      },
      floor: {
        roughness: +(floorMat?.roughness ?? 0).toFixed(2),
        metalness: +(floorMat?.metalness ?? 0).toFixed(2),
        color: floorMat ? '#' + floorMat.color.getHexString() : null,
        textureRepeat: floorTex ? { x: +floorTex.repeat.x.toFixed(1), y: +floorTex.repeat.y.toFixed(1) } : null,
        textureRotation: floorTex ? +(floorTex.rotation * 180 / Math.PI).toFixed(0) : 0,
        hasTexture: !!floorTex,
      },
      renderer: {
        shadowMapEnabled: entry.renderer.shadowMap.enabled,
        shadowMapType: entry.renderer.shadowMap.type === THREE.VSMShadowMap ? 'VSMShadowMap'
          : entry.renderer.shadowMap.type === THREE.PCFSoftShadowMap ? 'PCFSoftShadowMap' : 'other',
        toneMapping: entry.renderer.toneMapping === THREE.NoToneMapping ? 'NoToneMapping' : 'other',
      },
      roomDimensions: entry.roomDimensions,
    };
    const text = JSON.stringify(settings, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      console.log('%c[LightingPanel] Full settings copied to clipboard', 'color:#a6e3a1');
      console.log(text);
    });
  }, [entry, envMapEnabled, envMapIntensity, sceneBackground, selectedEnvIdx]);

  return (
    <>
    <div style={{
      position: 'fixed',
      top: '20px',
      left: '20px',
      zIndex: 99998,
      width: '300px',
      maxHeight: '90vh',
      overflowY: 'auto',
      background: '#1e1e2e',
      border: '1px solid #3b3b52',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#cdd6f4',
      animation: 'devtools-slide-in 0.2s ease-out',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', background: '#181825', borderBottom: '1px solid #3b3b52',
        borderRadius: '12px 12px 0 0',
      }}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>
          💡 Lighting {entry ? `(${entry.viewerType})` : ''}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', color: '#6c7086', fontSize: '16px',
            cursor: 'pointer', padding: '2px 6px', borderRadius: '4px',
          }}
        >✕</button>
      </div>

      {!entry ? (
        <div style={{ padding: '20px 14px', textAlign: 'center', color: '#6c7086', fontSize: '12px' }}>
          No active Room3DViewer with interior light found.<br />
          Navigate to a step with a full room visualizer.
        </div>
      ) : (
        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Light color */}
          <div>
            <div style={{ fontSize: '11px', color: '#a6adc8', marginBottom: '4px' }}>Light Color</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {COLOR_PRESETS.map(cp => (
                <button
                  key={cp.hex}
                  onClick={() => handleColorChange(cp.hex)}
                  style={{
                    width: '40px', height: '26px', borderRadius: '4px',
                    border: '#' + entry.light.color.getHexString() === cp.hex.toLowerCase()
                      ? '2px solid #89b4fa' : '1px solid #3b3b52',
                    background: cp.hex, cursor: 'pointer', fontSize: '8px',
                    color: '#333', fontWeight: 600, lineHeight: '26px', textAlign: 'center',
                  }}
                  title={cp.label}
                >{cp.label}</button>
              ))}
            </div>
            <div style={{ marginTop: '4px' }}>
              <input
                type="color"
                value={'#' + entry.light.color.getHexString()}
                onChange={e => handleColorChange(e.target.value)}
                style={{ width: '100%', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              />
            </div>
          </div>

          {/* Toggle shadows */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleToggleShadows}
              style={{
                padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                cursor: 'pointer', border: '1px solid #3b3b52',
                background: entry.light.castShadow ? '#a6e3a1' : '#45475a',
                color: entry.light.castShadow ? '#1e1e2e' : '#cdd6f4',
              }}
            >
              {entry.light.castShadow ? 'Shadows ON' : 'Shadows OFF'}
            </button>
          </div>

          {/* Sliders */}
          {SLIDERS.map(slider => {
            const val = slider.get(entry);
            return (
              <div key={slider.key}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  fontSize: '11px', color: '#a6adc8', marginBottom: '2px',
                }}>
                  <span>{slider.label}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#89b4fa' }}>
                    {val % 1 === 0 ? val : val.toFixed(2)}
                  </span>
                </div>
                <input
                  type="range"
                  min={slider.min}
                  max={slider.max}
                  step={slider.step}
                  value={val}
                  onChange={e => handleSliderChange(slider, parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: '#89b4fa', height: '16px' }}
                />
              </div>
            );
          })}

          {/* ---- Light Probe Section ---- */}
          <div style={{ borderTop: '1px solid #3b3b52', paddingTop: '10px', marginTop: '4px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#fab387', marginBottom: '8px' }}>
              🔮 Light Probe {entry.probe ? '' : '(loading HDR...)'}
            </div>

            {/* Env image selector — always show */}
            <div>
              <div style={{ fontSize: '11px', color: '#a6adc8', marginBottom: '4px' }}>Environment Image</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {ENV_IMAGES.map((img, idx) => (
                  <button
                    key={img.path}
                    onClick={() => handleEnvImageChange(idx)}
                    style={{
                      padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 500,
                      cursor: 'pointer', textAlign: 'left',
                      border: selectedEnvIdx === idx ? '1px solid #fab387' : '1px solid #3b3b52',
                      background: selectedEnvIdx === idx ? '#3b3b52' : '#242438',
                      color: selectedEnvIdx === idx ? '#fab387' : '#cdd6f4',
                    }}
                  >{img.label}</button>
                ))}
              </div>
            </div>

            {entry.probe && (
              <>
                {/* Probe intensity */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#a6adc8', marginBottom: '2px' }}>
                    <span>Probe Intensity</span>
                    <span style={{ fontFamily: 'monospace', color: '#89b4fa' }}>{probeIntensity.toFixed(2)}</span>
                  </div>
                  <input type="range" min={0} max={3} step={0.02}
                    value={probeIntensity}
                    onChange={e => handleProbeIntensity(parseFloat(e.target.value))}
                    style={{ width: '100%', accentColor: '#fab387', height: '16px' }}
                  />
                </div>

                {/* EnvMap toggle */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                  <button onClick={handleToggleEnvMap} style={{
                    padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                    cursor: 'pointer', border: '1px solid #3b3b52',
                    background: envMapEnabled ? '#fab387' : '#45475a',
                    color: envMapEnabled ? '#1e1e2e' : '#cdd6f4',
                  }}>
                    {envMapEnabled ? 'EnvMap ON' : 'EnvMap OFF'}
                  </button>
                  <button onClick={handleToggleBackground} style={{
                    padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                    cursor: 'pointer', border: '1px solid #3b3b52',
                    background: sceneBackground ? '#fab387' : '#45475a',
                    color: sceneBackground ? '#1e1e2e' : '#cdd6f4',
                  }}>
                    {sceneBackground ? 'HDR BG ON' : 'HDR BG OFF'}
                  </button>
                </div>

                {/* EnvMap intensity */}
                {envMapEnabled && (
                  <div style={{ marginTop: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#a6adc8', marginBottom: '2px' }}>
                      <span>EnvMap Intensity</span>
                      <span style={{ fontFamily: 'monospace', color: '#89b4fa' }}>{envMapIntensity.toFixed(2)}</span>
                    </div>
                    <input type="range" min={0} max={3} step={0.02}
                      value={envMapIntensity}
                      onChange={e => handleEnvMapIntensity(parseFloat(e.target.value))}
                      style={{ width: '100%', accentColor: '#fab387', height: '16px' }}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* ---- Material Overrides ---- */}
          <div style={{ borderTop: '1px solid #3b3b52', paddingTop: '10px', marginTop: '4px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#a6e3a1', marginBottom: '8px' }}>
              🎨 Material Properties
            </div>

            {/* Roughness */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#a6adc8', marginBottom: '2px' }}>
                <span>Roughness</span>
                <span style={{ fontFamily: 'monospace', color: '#89b4fa' }}>{matRoughness.toFixed(2)}</span>
              </div>
              <input type="range" min={0} max={1} step={0.02} value={matRoughness}
                onChange={e => handleMaterialRoughness(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#a6e3a1', height: '16px' }} />
            </div>

            {/* Metalness */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#a6adc8', marginBottom: '2px' }}>
                <span>Metalness</span>
                <span style={{ fontFamily: 'monospace', color: '#89b4fa' }}>{matMetalness.toFixed(2)}</span>
              </div>
              <input type="range" min={0} max={1} step={0.02} value={matMetalness}
                onChange={e => handleMaterialMetalness(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#a6e3a1', height: '16px' }} />
            </div>

            {/* Emissive Intensity */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#a6adc8', marginBottom: '2px' }}>
                <span>Emissive Intensity</span>
                <span style={{ fontFamily: 'monospace', color: '#89b4fa' }}>{matEmissive.toFixed(2)}</span>
              </div>
              <input type="range" min={0} max={1.5} step={0.02} value={matEmissive}
                onChange={e => handleEmissiveIntensity(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#a6e3a1', height: '16px' }} />
            </div>
          </div>

          {/* Floor textures button — room viewer only */}
          {entry.viewerType === 'room' && entry.floorMesh && (
            <button
              onClick={() => setFloorPanelOpen(v => !v)}
              style={{
                marginTop: '4px', padding: '8px', borderRadius: '6px', fontSize: '12px',
                fontWeight: 600, cursor: 'pointer', border: '1px solid #3b3b52',
                background: floorPanelOpen ? '#313244' : '#242438',
                color: '#fab387', textAlign: 'center',
              }}
            >
              🏗️ {floorPanelOpen ? 'Close' : 'Open'} Floor Textures
            </button>
          )}

          {/* Copy settings button */}
          <button
            onClick={copySettings}
            style={{
              marginTop: '4px', padding: '8px', borderRadius: '6px', fontSize: '12px',
              fontWeight: 600, cursor: 'pointer', border: '1px solid #3b3b52',
              background: '#242438', color: '#a6e3a1', textAlign: 'center',
            }}
          >
            📋 Copy Settings to Clipboard
          </button>
        </div>
      )}

      {/* Footer */}
      <div style={{
        padding: '6px 14px', fontSize: '10px', color: '#585b70', textAlign: 'center',
        borderTop: '1px solid #3b3b52', background: '#181825',
        borderRadius: '0 0 12px 12px',
      }}>
        {entry && (
          <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#89b4fa', marginBottom: '2px' }}>
            rotation: {'{'} x: {rotation.x.toFixed(3)}, y: {rotation.y.toFixed(3)} {'}'}
          </div>
        )}
        Adjustments apply to active Room3DViewer
      </div>
    </div>
    {floorPanelOpen && entry && (
      <FloorPanel entry={entry} onClose={() => setFloorPanelOpen(false)} />
    )}
    </>
  );
};
